import { createRouter, createWebHistory } from 'vue-router';
import { authorizationStore } from '@/store/authorization';
import { encode } from '@/assets/js/UrlCoding';
import { Capacitor } from '@capacitor/core';
import $localStorage from '@/capacitor/localStorageWrapper';
import authentication from './authentication';
import publicShoots from './publicShoots';
import appInsights from '@/AppInsights';
import account from './account';
import landers from './landers';
import app from './app';
import shooterAxios from '@/assets/js/axios/shooterSideAPI';

/**
 * Priority routes to preload components for improved loading speed
 * They also will not check for authentication in the router.beforeEach hook
 */
const priorityRoutes = [
	{
		name: '404 Not Found',
		path: '/404',
		name: '404',
		meta: {
			public: true,
			hideHeader: true,
			hideFooter: true,
		},
		component: () => import('../views/404.vue'),
	},
	{
		name: '',
		path: '/',
		component: () => import('../views/about-us-pages/home/Home.vue'),
		meta: {
			public: true,
			blockNative: true,
			lander: true,
			straightScoreFooter: true,
		},
	},
	{
		name: 'Features',
		path: '/features',
		component: () => import('../views/about-us-pages/features/Features.vue'),
		meta: {
			public: true,
			blockNative: true,
			lander: true,
			straightScoreFooter: true,
		},
	},
	{
		name: 'For Clubs',
		path: '/for-clubs',
		component: () => import('../views/about-us-pages/for-clubs/ForClubs.vue'),
		meta: {
			public: true,
			blockNative: true,
			lander: true,
			straightScoreFooter: true,
		},
	},
	{
		name: 'For Shooters',
		path: '/for-shooters',
		component: () =>
			import('../views/about-us-pages/for-shooters/ForShooters.vue'),
		meta: {
			public: true,
			blockNative: true,
			lander: true,
			straightScoreFooter: true,
		},
	},
	{
		name: 'About Us',
		path: '/about',
		component: () => import('../views/about-us-pages/about-us/AboutUs.vue'),
		meta: {
			public: true,
			blockNative: true,
			lander: true,
			straightScoreFooter: true,
		},
	},
];
const routes = [
	...priorityRoutes,
	{
		name: 'About',
		path: '/version',
		component: () => import('../views/Version.vue'),
		meta: {
			public: true,
		},
	},
	{
		name: 'Debug',
		path: '/debug',
		component: () => import('../views/Debug.vue'),
		meta: {
			public: true,
		},
	},
	...landers,
	...account,
	...authentication,
	...app,
	...publicShoots,
];

if (import.meta.env.VITE_MODE === 'development') {
	console.log('Router Routes: ', routes.length);
	const names = routes.map((r) => r.name);
	const duplicates = names.filter(
		(item, index) => names.indexOf(item) !== index
	);
	if (duplicates.length > 0) {
		throw new Error('Duplicate route names found: ' + duplicates.join(', '));
	}
}

let loginPath = sessionStorage.getItem('loginPath') || '/login';

shooterAxios.get('/use-login-form').then(({ data }) => {
	if (data.use) {
		if (!data.use.includes('/login') || data.use.split('/').length > 1)
			return console.error('Invalid login path: ', data.use);
		loginPath = data.use;
	}
});

const isNative = Capacitor.isNativePlatform();
const router = createRouter({
	history: createWebHistory(),
	scrollBehavior(to, from, savedPosition) {
		return { top: 0 };
	},
	routes,
});

let authStore = null;
let totalRoutes = 0;
const routesMap = routes.map((x) => x.path.toLowerCase());
const routesMapRegex = routes.map((x) =>
	x.path.toLowerCase().replace(/:\w+/gi, '([^/]+)')
);

// Preload priority route components to improve loading speed
priorityRoutes.forEach((route) => {
	if (!isNative && route.component && typeof route.component === 'function') {
		route.component().then(() => {
			// console.log(`Preloaded component for route: ${route.path}`);
		});
	}
});

router.beforeEach(async (to, from) => {
	totalRoutes++;
	console.log(`Navigating to: ${to.fullPath} from: ${from.fullPath}`);
	// check if it is a piority route
	const isPriorityRoute = priorityRoutes.some(
		(route) => route.path === to.path
	);
	if (isPriorityRoute && !isNative) {
		return true;
	}

	if (!authStore) {
		authStore = authorizationStore();
	}
	if (!authStore.initialized) {
		await authStore.init();
	}

	if (to.path.includes('/login') && to.path !== loginPath) {
		console.log('Login path redirecting: ', to.path, loginPath);

		return {
			replace: true,
			path: loginPath,
		};
	}

	if (isNative && totalRoutes === 1) {
		const isInitialized = await $localStorage.get('appInitialized');
		if (!isInitialized) {
			return {
				replace: false,
				path: '/app/login',
			};
		}
	}

	if (to.meta && (to.meta.blockNative || to.meta.lander)) {
		if (isNative) {
			if (authStore.isLoggedIn()) {
				return {
					replace: true,
					path: '/app',
				};
			} else {
				return {
					replace: true,
					path: loginPath,
				};
			}
		}
	}

	if (to.meta && to.meta.requireNative) {
		if (!isNative) {
			if (authStore.isLoggedIn()) {
				return {
					replace: true,
					path: loginPath,
				};
			} else {
				return {
					replace: true,
					path: '/app',
				};
			}
		}
	}

	if (!routesMap.includes(to.path.toLowerCase())) {
		const matchesRegex = routesMapRegex
			.map((x) => {
				const re = new RegExp(x);
				return re.test(to.path.toLowerCase());
			})
			.reduce((a, b) => {
				return a || b;
			});
		if (!matchesRegex) {
			console.debug('Redirect to: ', `${to.fullPath} (${encode(to.fullPath)})`);
			return {
				replace: true,
				path: '/404',
				query: {
					redirect: encode(from.fullPath),
				},
			};
		}
	}

	if (to.meta.public) return true;

	if (!authStore.isLoggedIn() && to.path != '/login') {
		console.debug('Redirect to: ', `${to.fullPath} (${encode(to.fullPath)})`);
		return {
			replace: true,
			path: loginPath,
			query: {
				redirect: encode(to.fullPath),
			},
		};
	}

	return true;
});

router.afterEach((to) => {
	if (!authStore) {
		authStore = authorizationStore();
	}
	if (!authStore.initialized) {
		authStore.init();
	}

	appInsights.trackPageView({
		properties: {
			pageType: to.meta.public ? 'public' : 'authenticated',
			user: authStore.userId,
			name: `${authStore.firstName} ${authStore.lastName}`,
		},
		name: to.name,
	});
	if (to.name) document.title = `${to.name} | ShootPro Cloud`;
	else document.title = `ShootPro Cloud`;

	try {
		if (to.meta.public) {
			document.querySelector('.grecaptcha-badge').style.display = 'block';
		} else {
			document.querySelector('.grecaptcha-badge').style.display = 'none';
		}
	} catch {}

	window.scrollTo(0, 0);
});

export default router;
